import { getMembershipPlanDisplayPricing } from '@frontend/api-client';
import type { Client, SetupIntent } from '@frontend/api-types';
import { match } from '@frontend/duck-tape';
import { useCallback, useEffect, useMemo, useState } from '@frontend/react';
import { usePaywallContext } from '@frontend/react-domain/paywall/PaywallContext/usePaywallContext';
import type { SetupIntent as StripeSetupIntent } from '@stripe/stripe-js';
import type { PaymentStepContextValue, PaymentStepDependencies } from './types';
import { useSetupIntent } from './useSetupIntent';

const getNormalizedPaymentError = (error: unknown) => {
  const errorRecord = typeof error === 'object' && error !== null ? (error as Record<string, unknown>) : undefined;
  const errorData =
    typeof errorRecord?.data === 'object' && errorRecord.data !== null
      ? (errorRecord.data as Record<string, unknown>)
      : undefined;
  const backendCode = typeof errorData?.code === 'number' ? errorData.code : undefined;
  const httpStatus = typeof errorRecord?.status === 'number' ? errorRecord.status : undefined;
  const errorCategory =
    backendCode === 4001
      ? 'validation'
      : httpStatus === 402
        ? 'payment'
        : typeof httpStatus === 'number' && httpStatus >= 500
          ? 'network'
          : 'unknown';

  return {
    backendCode,
    errorCategory,
    errorCode: backendCode ? `PAYMENT_${backendCode}` : 'PAYMENT_SUBMIT_FAILED',
    errorSource: 'paymentSubmit',
    httpStatus,
    retriable: backendCode !== 4001,
  } as const;
};

const getDisplayedPlanPrice = (plan: MaybeUndefined<{ discountPrice?: Cent; price: Cent }>) =>
  getMembershipPlanDisplayPricing(plan)?.primaryPrice;

/**
 * Cross-platform payment step hook that handles all payment business logic.
 *
 * Extracts the complete payment flow from PaymentStepContext while abstracting
 * platform-specific Stripe card handling through dependency injection.
 *
 * @param dependencies Platform-specific implementations injected from platform layers
 * @returns Payment step context value with all business logic
 *
 * @example
 * ```typescript
 * // Web usage
 * const dependencies = useWebPaymentDependencies();
 * const paymentStep = usePaymentStep(dependencies);
 *
 * // Mobile usage
 * const dependencies = useMobilePaymentDependencies();
 * const paymentStep = usePaymentStep(dependencies);
 * ```
 */
export const usePaymentStep = (dependencies: PaymentStepDependencies): PaymentStepContextValue => {
  const { paywallActions, paywallState } = usePaywallContext();

  // Extract all injected dependencies
  const {
    api: {
      useBuyCreditsMutation,
      useCreateSetupIntentMutation,
      useLazyListCardsQuery,
      useRetrieveClientQuery,
      useSetupIntentConfirmedMutation,
      useUpdatePlanMutation,
    },
    stripe: {
      cardFieldHandlers,
      cardFieldsComplete,
      confirmCardWithSetupIntent,
      confirmPlatformPayWithSetupIntent,
      isPlatformPaySupported: isPlatformPaySupportedFromDeps,
    },
    utilities: { handleMutation, track, trackAdvertising, useTrackingSlice },
  } = dependencies;
  const { imRef, irClickId } = useTrackingSlice();

  // Client data using injected hook
  const { data: client } = useRetrieveClientQuery();
  const [listCards] = useLazyListCardsQuery();

  // Payment mutations using injected hooks
  const [updatePlan] = useUpdatePlanMutation();
  const [createPaymentMethod] = useSetupIntentConfirmedMutation();
  const [buyCredits] = useBuyCreditsMutation();

  // Stripe setup intent management using injected cross-platform hook
  const { stripeSetupIntent } = useSetupIntent({
    handleMutation,
    useCreateSetupIntentMutation,
  });

  // Local state
  const [isStripeIntentLoading, setIsStripeIntentLoading] = useState(false);
  const [savedSetupIntent, setSavedSetupIntent] = useState<StripeSetupIntent>();
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [hasTrackedCardEntryCompleted, setHasTrackedCardEntryCompleted] = useState(false);
  const [hasTrackedCardEntryStarted, setHasTrackedCardEntryStarted] = useState(false);

  const firstPurchaseData = paywallState.purchase;
  const paywallSessionId = paywallState.navigation.paywallSessionId;
  const selectedPurchaseType = firstPurchaseData.selectedPurchaseType;
  const promotionTranslation = paywallState.promotionTranslation;

  const trackCardEntryStarted = useCallback(() => {
    if (hasTrackedCardEntryStarted || !selectedPurchaseType) {
      return;
    }

    setHasTrackedCardEntryStarted(true);
    track({
      data: {
        paywallSessionId,
        planCode: firstPurchaseData.selectedPlanData?.planCode,
        platform: 'web',
        purchaseType: selectedPurchaseType,
        step: paywallState.navigation.currentStep,
      },
      event: 'creditCardEntryStarted',
      topic: 'paywall',
    });
  }, [
    firstPurchaseData.selectedPlanData?.planCode,
    hasTrackedCardEntryStarted,
    paywallSessionId,
    paywallState.navigation.currentStep,
    selectedPurchaseType,
    track,
  ]);

  const allFieldsComplete = Boolean(cardFieldsComplete && stripeSetupIntent);

  const trackedCardFieldHandlers = useMemo(
    () => ({
      setCardCvcInfo: cardFieldHandlers.setCardCvcInfo
        ? (info: Parameters<NonNullable<typeof cardFieldHandlers.setCardCvcInfo>>[0]) => {
            trackCardEntryStarted();
            cardFieldHandlers.setCardCvcInfo?.(info);
          }
        : undefined,
      setCardExpiryInfo: cardFieldHandlers.setCardExpiryInfo
        ? (info: Parameters<NonNullable<typeof cardFieldHandlers.setCardExpiryInfo>>[0]) => {
            trackCardEntryStarted();
            cardFieldHandlers.setCardExpiryInfo?.(info);
          }
        : undefined,
      setCardNumberInfo: cardFieldHandlers.setCardNumberInfo
        ? (info: Parameters<NonNullable<typeof cardFieldHandlers.setCardNumberInfo>>[0]) => {
            trackCardEntryStarted();
            cardFieldHandlers.setCardNumberInfo?.(info);
          }
        : undefined,
    }),
    [cardFieldHandlers, trackCardEntryStarted],
  );

  useEffect(() => {
    if (!cardFieldsComplete || hasTrackedCardEntryCompleted || !selectedPurchaseType) {
      return;
    }

    setHasTrackedCardEntryCompleted(true);
    track({
      data: {
        paywallSessionId,
        planCode: firstPurchaseData.selectedPlanData?.planCode,
        platform: 'web',
        purchaseType: selectedPurchaseType,
        step: paywallState.navigation.currentStep,
      },
      event: 'creditCardEntryCompleted',
      topic: 'paywall',
    });
  }, [
    cardFieldsComplete,
    firstPurchaseData.selectedPlanData?.planCode,
    hasTrackedCardEntryCompleted,
    paywallSessionId,
    paywallState.navigation.currentStep,
    selectedPurchaseType,
    track,
  ]);

  const getSetupIntentFromStripe = useCallback(
    async (
      stripeSetupIntent: SetupIntent,
      client: Client,
      paymentMethodId?: string,
    ): Promise<MaybeUndefined<StripeSetupIntent>> =>
      handleMutation({
        errorBreadcrumb: {
          category: 'payment.ensureSetupIntent',
          message: 'Error occurred when ensuring setup intent',
        },
        mutation: async () => {
          // Use platform-specific Stripe SDK to confirm card setup
          // Takes backend SetupIntent (with stripeClientSecret) and returns Stripe SetupIntent (with id)
          const confirmedIntent = await confirmCardWithSetupIntent({
            backendSetupIntent: stripeSetupIntent, // Backend SetupIntent with stripeClientSecret
            client,
            onLoadingChange: setIsStripeIntentLoading,
            paymentMethodId,
          });

          setSavedSetupIntent(confirmedIntent);
          return confirmedIntent;
        },
        showSuccessToast: false,
      }),
    [confirmCardWithSetupIntent, handleMutation],
  );

  const getSetupIntent = useCallback(
    async (paymentMethodId?: string) => {
      // For Apple Pay, always confirm fresh (don't reuse saved intent from card flow)
      if (savedSetupIntent && !paymentMethodId) return savedSetupIntent;
      if (!stripeSetupIntent) {
        throw new Error('Stripe setup intent not found');
      }
      if (!client) {
        throw new Error('Client not found');
      }
      const setupIntent = await getSetupIntentFromStripe(stripeSetupIntent, client, paymentMethodId);

      if (!setupIntent) {
        throw new Error('Setup intent not found');
      }

      return setupIntent;
    },
    [client, getSetupIntentFromStripe, savedSetupIntent, stripeSetupIntent],
  );

  const submitPayment = useCallback(
    async (paymentMethodId?: string): Promise<boolean> => {
      if (isPaymentProcessing || !selectedPurchaseType) {
        return false;
      }

      const mutationResult = await handleMutation({
        errorBreadcrumb: {
          category: 'payment.submitPayment',
          message: 'Error occurred when submitting payment',
        },
        errorMessage: 'Error occurred when submitting payment, please try again.',
        mutation: async () => {
          setIsPaymentProcessing(true);

          const setupIntent = await getSetupIntent(paymentMethodId);

          await createPaymentMethod({
            isDefault: true,
            setupIntentId: setupIntent.id,
            skipInvalidate: true,
          }).unwrap();

          await match(selectedPurchaseType)
            .with('Subscription', async () => {
              await updatePlan({
                imRef,
                irClickId,
                plan: firstPurchaseData.selectedPlanData!.planCode,
                promotionCode: promotionTranslation.promoCode,
              }).unwrap();
            })
            .with('ALaCarteCredits', async () => {
              const cards = await listCards().unwrap();

              const defaultCard = cards?.find((card: { id: string; isDefault: boolean }) => card.isDefault);
              if (defaultCard) {
                await buyCredits({
                  amountOfCredits: firstPurchaseData.selectedCreditsPackage!.amount,
                  cardId: defaultCard.id,
                  totalPrice: firstPurchaseData.selectedCreditsPackage!.priceCent,
                }).unwrap();
              }
            })
            .exhaustive();

          return { success: true } as const;
        },
        onComplete: () => {
          setIsPaymentProcessing(false);
        },
        onError: (error) => {
          if (!selectedPurchaseType) {
            return;
          }

          track({
            data: {
              ...getNormalizedPaymentError(error),
              paywallSessionId,
              planCode: firstPurchaseData.selectedPlanData?.planCode,
              platform: 'web',
              purchaseType: selectedPurchaseType,
              step: paywallState.navigation.currentStep,
            },
            event: 'paymentSubmitFailed',
            topic: 'paywall',
          });
        },
        onSuccess: () => {
          track({
            data: {
              paywallSessionId,
              planCode: firstPurchaseData.selectedPlanData?.planCode,
              platform: 'web',
              purchaseType: selectedPurchaseType,
              step: paywallState.navigation.currentStep,
            },
            event: 'paymentSubmitSucceeded',
            topic: 'paywall',
          });

          // Track purchase completion with purchase details
          const trackingData = match(selectedPurchaseType)
            .with('Subscription', () => ({
              currency: 'USD',
              planCode: firstPurchaseData.selectedPlanData?.planCode,
              planName: firstPurchaseData.selectedPlanData?.label,
              purchaseType: selectedPurchaseType,
              value: (() => {
                const displayPrice = getDisplayedPlanPrice(firstPurchaseData.selectedPlanData);

                return displayPrice !== undefined ? displayPrice / 100 : undefined;
              })(),
            }))
            .with('ALaCarteCredits', () => ({
              creditAmount: firstPurchaseData.selectedCreditsPackage?.amount,
              currency: 'USD',
              purchaseType: selectedPurchaseType,
              totalPrice: firstPurchaseData.selectedCreditsPackage?.priceCent,
              value: firstPurchaseData.selectedCreditsPackage
                ? firstPurchaseData.selectedCreditsPackage.priceCent / 100
                : undefined,
            }))
            .otherwise(() => ({ purchaseType: selectedPurchaseType }));

          track({
            data: trackingData,
            event: 'purchaseCompleted',
            topic: 'paywall',
          });
          if (trackAdvertising) trackAdvertising('Purchase', trackingData);
          paywallActions.navigation.incrementStep();
        },
        showSuccessToast: false,
      });

      return Boolean(mutationResult);
    },
    [
      buyCredits,
      createPaymentMethod,
      firstPurchaseData,
      getSetupIntent,
      handleMutation,
      imRef,
      irClickId,
      isPaymentProcessing,
      listCards,
      paywallActions,
      paywallSessionId,
      paywallState.navigation.currentStep,
      promotionTranslation,
      selectedPurchaseType,
      track,
      trackAdvertising,
      updatePlan,
    ],
  );

  const isPlatformPaySupported = isPlatformPaySupportedFromDeps ?? false;

  const submitPlatformPayPayment = useCallback(
    async (creditsUnitTerm?: string): Promise<boolean> => {
      if (
        isPaymentProcessing ||
        !selectedPurchaseType ||
        !stripeSetupIntent ||
        !client ||
        !confirmPlatformPayWithSetupIntent
      ) {
        return false;
      }

      const amountInCents =
        selectedPurchaseType === 'Subscription'
          ? getDisplayedPlanPrice(firstPurchaseData.selectedPlanData) ?? 0
          : firstPurchaseData.selectedCreditsPackage?.priceCent ?? 0;

      const label =
        selectedPurchaseType === 'Subscription' ? 'Duckbill Membership' : `Duckbill ${creditsUnitTerm ?? 'Credits'}`;

      setIsPaymentProcessing(true);

      const platformPayResult = await confirmPlatformPayWithSetupIntent({
        amountInCents,
        clientSecret: stripeSetupIntent.stripeClientSecret,
        label,
      });

      if ('cancelled' in platformPayResult || 'error' in platformPayResult) {
        setIsPaymentProcessing(false);
        return false;
      }

      const { setupIntentId } = platformPayResult;

      const mutationResult = await handleMutation({
        errorBreadcrumb: {
          category: 'payment.submitWalletPayment',
          message: 'Error occurred when submitting wallet payment',
        },
        errorMessage: 'Wallet payment failed, please try again.',
        mutation: async () => {
          await createPaymentMethod({
            isDefault: true,
            setupIntentId,
            skipInvalidate: true,
          }).unwrap();

          await match(selectedPurchaseType)
            .with('Subscription', async () => {
              await updatePlan({
                imRef,
                irClickId,
                plan: firstPurchaseData.selectedPlanData!.planCode,
                promotionCode: promotionTranslation.promoCode,
              }).unwrap();
            })
            .with('ALaCarteCredits', async () => {
              const cards = await listCards().unwrap();
              const defaultCard = cards?.find((card: { id: string; isDefault: boolean }) => card.isDefault);
              if (!defaultCard) {
                throw new Error('No default card found after Apple Pay setup intent confirmation');
              }
              await buyCredits({
                amountOfCredits: firstPurchaseData.selectedCreditsPackage!.amount,
                cardId: defaultCard.id,
                totalPrice: firstPurchaseData.selectedCreditsPackage!.priceCent,
              }).unwrap();
            })
            .exhaustive();

          return true as const;
        },
        onComplete: () => {
          setIsPaymentProcessing(false);
        },
        onError: (error) => {
          if (!selectedPurchaseType) {
            return;
          }

          track({
            data: {
              ...getNormalizedPaymentError(error),
              paywallSessionId,
              planCode: firstPurchaseData.selectedPlanData?.planCode,
              platform: 'mobile',
              purchaseType: selectedPurchaseType,
              step: paywallState.navigation.currentStep,
            },
            event: 'paymentSubmitFailed',
            topic: 'paywall',
          });
        },
        onSuccess: () => {
          track({
            data: {
              paywallSessionId,
              planCode: firstPurchaseData.selectedPlanData?.planCode,
              platform: 'mobile',
              purchaseType: selectedPurchaseType,
              step: paywallState.navigation.currentStep,
            },
            event: 'paymentSubmitSucceeded',
            topic: 'paywall',
          });

          const trackingData = match(selectedPurchaseType)
            .with('Subscription', () => ({
              currency: 'USD',
              planCode: firstPurchaseData.selectedPlanData?.planCode,
              planName: firstPurchaseData.selectedPlanData?.label,
              purchaseType: selectedPurchaseType,
              value: (() => {
                const displayPrice = getDisplayedPlanPrice(firstPurchaseData.selectedPlanData);

                return displayPrice !== undefined ? displayPrice / 100 : undefined;
              })(),
            }))
            .with('ALaCarteCredits', () => ({
              creditAmount: firstPurchaseData.selectedCreditsPackage?.amount,
              currency: 'USD',
              purchaseType: selectedPurchaseType,
              totalPrice: firstPurchaseData.selectedCreditsPackage?.priceCent,
              value: firstPurchaseData.selectedCreditsPackage
                ? firstPurchaseData.selectedCreditsPackage.priceCent / 100
                : undefined,
            }))
            .otherwise(() => ({ purchaseType: selectedPurchaseType }));

          track({ data: trackingData, event: 'purchaseCompleted', topic: 'paywall' });
          if (trackAdvertising) trackAdvertising('Purchase', trackingData);
          paywallActions.navigation.incrementStep();
        },
        showSuccessToast: false,
      });

      return Boolean(mutationResult);
    },
    [
      buyCredits,
      client,
      createPaymentMethod,
      confirmPlatformPayWithSetupIntent,
      firstPurchaseData,
      handleMutation,
      imRef,
      irClickId,
      isPaymentProcessing,
      listCards,
      paywallActions,
      paywallSessionId,
      paywallState.navigation.currentStep,
      promotionTranslation,
      selectedPurchaseType,
      stripeSetupIntent,
      track,
      trackAdvertising,
      updatePlan,
    ],
  );

  const completePaymentAfterWalletConfirm = useCallback(
    async (setupIntentId: string): Promise<boolean> => {
      if (isPaymentProcessing || !selectedPurchaseType) return false;

      const mutationResult = await handleMutation({
        errorBreadcrumb: {
          category: 'payment.completeWalletPayment',
          message: 'Error occurred when completing wallet payment',
        },
        errorMessage: 'Wallet payment failed, please try again.',
        mutation: async () => {
          setIsPaymentProcessing(true);

          await createPaymentMethod({
            isDefault: true,
            setupIntentId,
            skipInvalidate: true,
          }).unwrap();

          await match(selectedPurchaseType)
            .with('Subscription', async () => {
              await updatePlan({
                imRef,
                irClickId,
                plan: firstPurchaseData.selectedPlanData!.planCode,
                promotionCode: promotionTranslation.promoCode,
              }).unwrap();
            })
            .with('ALaCarteCredits', async () => {
              const cards = await listCards().unwrap();
              const defaultCard = cards?.find((card: { id: string; isDefault: boolean }) => card.isDefault);
              if (!defaultCard) {
                throw new Error('No default card found after wallet payment');
              }
              await buyCredits({
                amountOfCredits: firstPurchaseData.selectedCreditsPackage!.amount,
                cardId: defaultCard.id,
                totalPrice: firstPurchaseData.selectedCreditsPackage!.priceCent,
              }).unwrap();
            })
            .exhaustive();

          return true as const;
        },
        onComplete: () => {
          setIsPaymentProcessing(false);
        },
        onError: (error) => {
          if (!selectedPurchaseType) return;
          track({
            data: {
              ...getNormalizedPaymentError(error),
              paywallSessionId,
              planCode: firstPurchaseData.selectedPlanData?.planCode,
              platform: 'web',
              purchaseType: selectedPurchaseType,
              step: paywallState.navigation.currentStep,
            },
            event: 'paymentSubmitFailed',
            topic: 'paywall',
          });
        },
        onSuccess: () => {
          track({
            data: {
              paywallSessionId,
              planCode: firstPurchaseData.selectedPlanData?.planCode,
              platform: 'web',
              purchaseType: selectedPurchaseType,
              step: paywallState.navigation.currentStep,
            },
            event: 'paymentSubmitSucceeded',
            topic: 'paywall',
          });

          const trackingData = match(selectedPurchaseType)
            .with('Subscription', () => ({
              currency: 'USD',
              planCode: firstPurchaseData.selectedPlanData?.planCode,
              planName: firstPurchaseData.selectedPlanData?.label,
              purchaseType: selectedPurchaseType,
              value: firstPurchaseData.selectedPlanData ? firstPurchaseData.selectedPlanData.price / 100 : undefined,
            }))
            .with('ALaCarteCredits', () => ({
              creditAmount: firstPurchaseData.selectedCreditsPackage?.amount,
              currency: 'USD',
              purchaseType: selectedPurchaseType,
              totalPrice: firstPurchaseData.selectedCreditsPackage?.priceCent,
              value: firstPurchaseData.selectedCreditsPackage
                ? firstPurchaseData.selectedCreditsPackage.priceCent / 100
                : undefined,
            }))
            .otherwise(() => ({ purchaseType: selectedPurchaseType }));

          track({ data: trackingData, event: 'purchaseCompleted', topic: 'paywall' });
          if (trackAdvertising) trackAdvertising('Purchase', trackingData);
          paywallActions.navigation.incrementStep();
        },
        showSuccessToast: false,
      });

      return Boolean(mutationResult);
    },
    [
      buyCredits,
      createPaymentMethod,
      firstPurchaseData,
      handleMutation,
      imRef,
      irClickId,
      isPaymentProcessing,
      listCards,
      paywallActions,
      paywallSessionId,
      paywallState.navigation.currentStep,
      promotionTranslation,
      selectedPurchaseType,
      track,
      trackAdvertising,
      updatePlan,
    ],
  );

  return useMemo(
    () => ({
      allFieldsComplete,
      completePaymentAfterWalletConfirm,
      isPaymentProcessing,
      isPlatformPaySupported,
      isStripeIntentLoading,
      ...trackedCardFieldHandlers,
      stripeClientSecret: stripeSetupIntent?.stripeClientSecret,
      submitPayment,
      submitPlatformPayPayment,
      track,
    }),
    [
      allFieldsComplete,
      completePaymentAfterWalletConfirm,
      isPlatformPaySupported,
      isPaymentProcessing,
      isStripeIntentLoading,
      stripeSetupIntent,
      trackedCardFieldHandlers,
      submitPlatformPayPayment,
      submitPayment,
      track,
    ],
  );
};
